import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Mail, Phone, Star } from "lucide-react";
import { useSavedViews } from "@/lib/useSavedViews";
import { useToast } from "@/context/ToastContext";
import { useCallRecord } from "@/components/calling/CallLink";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, unwrapObject } from "@/lib/api";
import { formatDateTime } from "@/lib/dateTime";
import { formatINR } from "@/lib/utils";
import type { PreviewEntity } from "./PreviewContext";

interface Preview {
  entity: PreviewEntity;
  id: string;
  title: string;
  subtitle: string;
  status?: string;
  owner: string;
  link: string;
  facts: { label: string; value: string | number }[];
  tasks: { id: string; title: string; task_type: string; due_at: string | null }[];
  activity: { type: string; title: string; at: string | null }[];
  has_more?: boolean;
}

const MONEY = new Set(["Amount", "Weighted", "Budget"]);
const pretty = (s: string) => s.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

/** The quick look: headline, key facts, next tasks and latest activity, with Open / Call / Email */
export function PreviewSheet({ target, onClose }: { target: { entity: PreviewEntity; id: string } | null; onClose: () => void }) {
  const [data, setData] = useState<Preview | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);
  const { views: favourites, save: saveFavourite, remove: removeFavourite } = useSavedViews("favourite");
  const { error: toastError } = useToast();
  const callRecord = useCallRecord();
  const favourite = data ? favourites.find((v) => v.name === `${data.entity}:${data.id}`) : undefined;
  const toggleFavourite = async () => {
    if (!data) return;
    try {
      if (favourite) await removeFavourite(favourite.id);
      else await saveFavourite(`${data.entity}:${data.id}`, { entity: data.entity, id: data.id, label: data.title, sub: data.subtitle, to: data.link });
    } catch (e: any) {
      toastError("Couldn't update favourites", e?.message || "Try again");
    }
  };

  useEffect(() => setMore(false), [target?.entity, target?.id]);

  useEffect(() => {
    if (!target) return;
    let live = true;
    setData((d) => (more ? d : null));
    setErr("");
    setLoading(true);
    api
      .get(`/api/Preview/${target.entity}/${target.id}${more ? "?more=1" : ""}`)
      .then((r) => live && setData(unwrapObject<Preview>(r)))
      .catch((e) => live && setErr(e?.message || "Couldn't load this record"))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [target?.entity, target?.id, more]); // eslint-disable-line react-hooks/exhaustive-deps

  const fact = (label: string) => data?.facts.find((f) => f.label === label)?.value;
  const phone = fact("Phone");
  const email = fact("Email");
  const show = (f: { label: string; value: string | number }) => {
    if (MONEY.has(f.label)) return formatINR(Number(f.value));
    if (f.label === "Follow-up" || f.label === "Expected close") return formatDateTime(String(f.value), { day: "numeric", month: "short", year: "numeric" });
    if (f.label === "Priority") return pretty(String(f.value));
    return String(f.value);
  };

  return (
    <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="border-b border-border pr-10">
          <SheetTitle className="truncate">{data?.title || (loading ? "Loading…" : "Quick look")}</SheetTitle>
          <SheetDescription className="truncate">{data ? [data.subtitle, data.status && pretty(data.status)].filter(Boolean).join(" · ") || " " : " "}</SheetDescription>
        </SheetHeader>

        {err && <p className="p-4 text-sm text-destructive">{err}</p>}
        {data && (
          <div className="space-y-5 p-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={toggleFavourite} aria-pressed={!!favourite} aria-label={favourite ? "Remove from favourites" : "Add to favourites"}>
                <Star className={`mr-1.5 size-3.5 ${favourite ? "fill-current text-gold-ink" : ""}`} /> {favourite ? "Starred" : "Star"}
              </Button>
              <Button asChild size="sm" onClick={onClose}>
                <Link to={data.link}>
                  <ExternalLink className="mr-1.5 size-3.5" /> Open
                </Link>
              </Button>
              {phone && (
                <Button size="sm" variant="outline" onClick={() => void callRecord(data.entity.toUpperCase() as "LEAD" | "CONTACT" | "DEAL" | "ACCOUNT", data.id, String(phone))}>
                  <Phone className="mr-1.5 size-3.5" /> Call
                </Button>
              )}
              {email && (
                <Button asChild size="sm" variant="outline">
                  <a href={`mailto:${email}`}>
                    <Mail className="mr-1.5 size-3.5" /> Email
                  </a>
                </Button>
              )}
            </div>

            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
              {data.owner && (
                <>
                  <dt className="text-muted-foreground">Owner</dt>
                  <dd className="min-w-0 truncate font-medium">{data.owner}</dd>
                </>
              )}
              {data.facts.map((f) => (
                <React.Fragment key={f.label}>
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd className="min-w-0 break-words font-medium">{show(f)}</dd>
                </React.Fragment>
              ))}
            </dl>

            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Next tasks</h3>
              {data.tasks.length ? (
                <ul className="space-y-1.5">
                  {data.tasks.map((t) => (
                    <li key={t.id} className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate">{t.title}</span>
                      {t.due_at && <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(t.due_at)}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No open tasks</p>
              )}
            </section>

            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Latest activity</h3>
              {data.activity.length ? (
                <ul className="space-y-1.5">
                  {data.activity.map((a, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate">
                        <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{pretty(a.type || "note")}</span>
                        {a.title}
                      </span>
                      {a.at && <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(a.at)}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No activity yet</p>
              )}
              {data.has_more && !more && (
                <button type="button" onClick={() => setMore(true)} className="mt-1.5 text-xs font-medium text-primary hover:underline">
                  Show more
                </button>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
